import React, { useEffect, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useParams, useNavigate } from "react-router-dom";
import createAxiosInstance from "../utility/utils";
import {
  Spinner,
  Container,
  Breadcrumb,
  Col,
  Row,
  Table,
  Button,
  Form,
} from "react-bootstrap";
import { ExternalLink } from "lucide-react";

export default function TeacherSessions() {
  const navigate = useNavigate();
  const { teacherId } = useParams();
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [filteredSessions, setFilteredSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // filters and pagination
  const [subjectFilter, setSubjectFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [subjectSuggestions, setSubjectSuggestions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const axiosInstance = createAxiosInstance(getToken);

  useEffect(() => {
    if (isLoaded && isSignedIn) {
      const fetchSessions = async () => {
        try {
          const response = await axiosInstance.get(
            `/api/admin/teacher/${teacherId}/sessions`
          );

          // Find the specific teacher by their teacherId
          const teacherData = response.data.teacherAttendance.find(
            (teacher) => teacher.teacherId === teacherId
          );

          if (teacherData && teacherData.sessions) {
            setSessions(teacherData.sessions);
            setFilteredSessions(teacherData.sessions);
          } else {
            setSessions([]);
            setFilteredSessions([]);
          }

          setIsLoading(false);
        } catch (err) {
          setIsLoading(false);
        }
      };

      fetchSessions();
    }
  }, [isLoaded, isSignedIn, teacherId]);

  // Handle subject filter input
  const handleSubjectFilter = (e) => {
    const value = e.target.value;
    setSubjectFilter(value);

    // Generate subject suggestions
    if (value) {
      const suggestions = [
        ...new Set(
          sessions
            .map((session) => session.subject)
            .filter(
              (subject) =>
                subject && subject.toLowerCase().includes(value.toLowerCase())
            )
        ),
      ];
      setSubjectSuggestions(suggestions);
    } else {
      setSubjectSuggestions([]);
    }

    applyFilters(value, dateFilter);
  };

  // Handle date filter input
  const handleDateFilter = (e) => {
    const value = e.target.value;
    setDateFilter(value);
    applyFilters(subjectFilter, value);
  };

  // Apply both filters
  const applyFilters = (subject, date) => {
    let filtered = [...sessions];

    if (subject) {
      filtered = filtered.filter(
        (session) =>
          session.subject &&
          session.subject.toLowerCase().includes(subject.toLowerCase())
      );
    }

    if (date) {
      filtered = filtered.filter((session) => session.date === date);
    }

    setFilteredSessions(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Select suggestion
  const handleSelectSuggestion = (subject) => {
    setSubjectFilter(subject);
    setSubjectSuggestions([]);
    applyFilters(subject, dateFilter);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredSessions.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredSessions.length / itemsPerPage);

  const handleSessionClick = async (session) => {
    try {
      const response = await axiosInstance.get(
        `/api/attendance/session/${session.sessionId}`
      );
      navigate("/attendance-records", {
        state: {
          batchName: session?.batch || "unknown batch",
          subjectName: session?.subject || "unknown subject",
          date: session.date,
          time: session.time,
          sessionId: session.sessionId,
          attendanceRecords: response.data.attendanceRecords,
        },
      });
    } catch (err) {}
  };

  if (isLoading) {
    return (
      <Container className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="d-flex justify-content-center align-items-center">
          <div className="spinner-border" role="status">
            <span className="sr-only"></span>
          </div>
          &nbsp;&nbsp;
          <p className="ml-3 mb-0 align-self-center">Loading... Please wait</p>
        </div>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Breadcrumb>
        <Breadcrumb.Item href="/">Apps</Breadcrumb.Item>
        <Breadcrumb.Item href="/teachers">Teachers</Breadcrumb.Item>
        <Breadcrumb.Item active>Teacher Sessions</Breadcrumb.Item>
      </Breadcrumb>

      {/* Filters */}
      <Row className="mb-3">
        <Col md={4}>
          <Form.Group className="position-relative">
            <Form.Label>Filter by Subject</Form.Label>
            <Form.Control
              type="text"
              placeholder="Type to search subjects..."
              value={subjectFilter}
              onChange={handleSubjectFilter}
            />
            {subjectSuggestions.length > 0 && (
              <div
                className="position-absolute w-100 bg-white border rounded mt-1"
                style={{ zIndex: 1000 }}
              >
                {subjectSuggestions.map((subject, index) => (
                  <div
                    key={index}
                    className="p-2 cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSelectSuggestion(subject)}
                  >
                    {subject}
                  </div>
                ))}
              </div>
            )}
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Filter by Date</Form.Label>
            <Form.Control
              type="date"
              value={dateFilter}
              onChange={handleDateFilter}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col>
          <div className="border rounded-lg shadow-sm">
            <Table hover responsive className="mb-0">
              <thead className="bg-gray-50">
                <tr>
                  <th>Batch</th>
                  <th>Subject</th>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((session) => (
                    <tr key={session.sessionId}>
                      <td>{session.batch || "N/A"}</td>
                      <td>{session.subject || "N/A"}</td>
                      <td>{session.date}</td>
                      <td>{session.time}</td>
                      <td>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => handleSessionClick(session)}
                        >
                          <ExternalLink className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center">
                      No sessions found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Col>
      </Row>

      {/* Pagination */}
      {totalPages > 1 && (
        <Row className="mt-3">
          <Col className="d-flex justify-content-center">
            <Button
              variant="outline-primary"
              className="mx-1"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <span className="mx-3 align-self-center">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline-primary"
              className="mx-1"
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </Col>
        </Row>
      )}
    </Container>
  );
}
